import {GUID} from "@utils/AppConstants";
import {useEffect, useMemo, useRef, useState} from "react";
import FlintLoadingWrapper from "@components/page_components/common/loading/FlintLoadingWrapper";
import axios from "axios";
import {
    OrderRecipeComponentEditModel, OrderRecipeComponentSaveRequest, OrderRecipeEditModel,
    OrderRecipeEditModuleModel, OrderRecipePreviewModel,
    OrderRecipeSaveRequest
} from "@pages/orders/order.models";
import {ApiUrl_Orders_GetOrderRecipe, ApiUrl_Orders_SaveOrderRecipe} from "@utils/Endpoints";
import {ApiResponseDto, NameWithId} from "@utils/Models";
import {useFlintNotificationContext} from "@components/page_components/common/notifications/FlintNotificationContext";
import FlintFieldset from "@components/layout/FlintFieldset";
import {useTranslation} from "react-i18next";
import FlintTextBox from "@components/controls/FlintTextBox";
import FlintDropDownList from "@components/controls/FlintDropDownList";
import OrderRecipeComponentEdit from "@pages/orders/preview/recipes/edit/OrderRecipeComponentEdit";
import FlintButton_Add from "@components/controls/buttons/FlintButton_Add";
import {CommonUtils} from "@utils/CommonUtils";
import NewGuid = CommonUtils.NewGuid;
import FlintButton_Save from "@components/controls/buttons/FlintButton_Save";
import * as Yup from "yup"
import useVivoValidation from "@utils/validation/useVivoValidation";
import {WindowActionsBar} from "@progress/kendo-react-dialogs";
import {Switch} from "@progress/kendo-react-inputs";
import FlintButton_Cancel from "@components/controls/buttons/FlintButton_Cancel";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClipboardList, faFlask, faInfoCircle, faWandMagicSparkles} from "@fortawesome/free-solid-svg-icons";
import FlintButton from "@components/controls/FlintButton";
import {useVivoPopupContext} from "@utils/popups/VivoPopupContext";
import styles from "./OrderRecipeEditPanel.module.scss";

export default function OrderRecipeEditPanel(props: OrderRecipeEditComponentProps) {
    const {state, utils, handlers} = useOrderRecipeEditComponent(props);
    const {t} = useTranslation();
    return (
        <div className={"dialog-content-wrapper"}>
            <FlintFieldset marginBottom={0}>
                <FlintLoadingWrapper showLoading={state.isLoading.get}>
                    <div className={styles.sectionHeader}>
                        <FontAwesomeIcon icon={faClipboardList}/>
                        {t("ORDER_PREVIEW_RecipeEditPanelTitle")}
                    </div>
                    <div className={"row"}>
                        <div className={"col-md-3 form-group"}>
                            <FlintTextBox
                                maxLength={50}
                                value={state.recipe.get?.CodeGeneratorNumber}
                                onChange={x => handlers.handleOnRecipeUpdated("CodeGeneratorNumber", x.value)}
                                validationData={{
                                    validationMessage: utils.validators.recipeValidator.formErrors?.CodeGeneratorNumber,
                                    validateFunction: utils.validators.recipeValidator.validateForm,
                                    optionalObjectToValidate: state.recipe.get,
                                }}
                                label={t("ORDER_RECIPE_CodeGeneratorNumber")}/>
                            {!state.recipe.get?.CodeGeneratorNumber && (
                                <small className={"text-muted d-block mt-1"}>
                                    {t("ORDER_RECIPE_CodeGeneratorNumberHelperEmpty")}
                                </small>
                            )}
                        </div>
                        {state.recipe.get?.SapCode !== null && state.recipe.get?.SapCode !== undefined && (
                            <div className={"col-md-3 form-group"}>
                                <FlintTextBox
                                    value={state.recipe.get.SapCode}
                                    onChange={x => handlers.handleOnRecipeUpdated("SapCode", x.value)}
                                    validationData={{
                                        validationMessage: utils.validators.recipeValidator.formErrors?.SapCode,
                                        validateFunction: utils.validators.recipeValidator.validateForm,
                                        optionalObjectToValidate: state.recipe.get,
                                    }}
                                    maxLength={8}
                                    label={t("ORDER_RECIPE_SapCode")}/>
                            </div>
                        )}
                        <div className={"col-md-3 form-group"}>
                            <FlintDropDownList<NameWithId>
                                required={true}
                                dataSource={utils.unitsOfMeasure}
                                selectedValue={state.recipe.get?.UnitOfMeasure}
                                onItemSelected={x => handlers.handleOnRecipeUpdated("UnitOfMeasure", x)}
                                validationData={{
                                    validationMessage: utils.validators.recipeValidator.formErrors?.["UnitOfMeasure.Id"]
                                        ?? utils.validators.recipeValidator.formErrors?.UnitOfMeasure,
                                    validateFunction: utils.validators.recipeValidator.validateForm,
                                    optionalObjectToValidate: state.recipe.get,
                                }}
                                label={t("COMMON_Unit")}/>
                        </div>
                        <div className={"col-md-12 form-group"}>
                            <div className={styles.descriptionField}>
                                <div className={styles.descriptionInput}>
                                    <FlintTextBox value={state.recipe.get?.Description}
                                                  onChange={x => handlers.handleOnRecipeUpdated("Description", x.value)}
                                                  maxLength={255}
                                                  label={t("RECIPES_FormulaDescription")}/>
                                </div>
                                <FlintButton fillMode={"outline"}
                                             themeColor={"primary"}
                                             onClick={handlers.handleGenerateDescriptionClicked}>
                                    <FontAwesomeIcon icon={faWandMagicSparkles} className={"me-1"}/>
                                    {t("RECIPES_DescriptionGenerator_OpenButton")}
                                </FlintButton>
                            </div>
                        </div>
                    </div>
                    <div className={styles.componentsSection}>
                        <div className={styles.sectionHeader}>
                            <FontAwesomeIcon icon={faFlask}/>
                            {t("RECIPES_Components")}
                            {utils.hasSeriesContext && (
                                <>
                                    <span className={styles.sectionHeaderDivider}/>
                                    <span className={"d-flex align-items-center gap-2 fs-6 fw-normal"} title={t("ORDER_RECIPE_SearchFullDatabase_Hint")}>
                                        <Switch checked={state.isFullSearch.get}
                                                onChange={e => handlers.handleFullSearchToggled(e.value)}
                                                size={"small"}/>
                                        {t("ORDER_RECIPE_SearchFullDatabase")}
                                    </span>
                                </>
                            )}
                        </div>
                        <div className={"recipe-components"}>
                            {state.recipe.get?.Components.map((c, i) => (
                                <OrderRecipeComponentEdit key={c.Guid}
                                                          TotalAmount={state.totalAmount.get}
                                                          OnComponentDeleted={handlers.handleComponentDeleted}
                                                          OnComponentUpdated={handlers.handleOnComponentUpdated}
                                                          Recipe={state.recipe.get!}
                                                          ComponentIndex={i}
                                                          ComponentPickerDisabled={utils.isLabDivisionMismatch}
                                                          SeriesId={utils.effectiveSeriesId}
                                                          IgnoreSeriesFilter={state.isFullSearch.get}
                                                          recipeValidator={utils.validators.recipeValidator}/>
                            ))}
                        </div>
                        {state.totalAmount.get > 0 && (
                            <div className={styles.totalRow}>
                                <span>{t("total")}:</span>
                                <span className={styles.totalValue}>
                                    {state.totalAmount.get.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}&nbsp;{state.recipe.get?.UnitOfMeasure?.Name}
                                </span>
                            </div>
                        )}
                        {utils.isLabDivisionMismatch && (
                            <div className={"alert alert-warning d-flex align-items-center gap-2 py-2 mt-2"}>
                                <FontAwesomeIcon icon={faInfoCircle}/>
                                {t("ORDER_RECIPES_LabDivisionMismatchWarning")}
                            </div>
                        )}
                        <div className={"d-flex justify-content-center mt-2"}>
                            <FlintButton_Add fillMode={"outline"} disabled={utils.isLabDivisionMismatch} onClick={handlers.handleAddNewComponentClicked}
                                             buttonText={t("COMPONENTS_AddNew")}/>
                        </div>
                    </div>
                </FlintLoadingWrapper>
            </FlintFieldset>
            <div className={"dialog-actions"}>
                <WindowActionsBar layout={"center"}>
                    <FlintButton_Cancel disabled={state.isLoading.get} onClick={handlers.handleCancelClicked}/>
                    <FlintButton_Save disabled={state.isLoading.get}
                                      onClick={handlers.handleSaveRecipeClicked}/>
                </WindowActionsBar>
            </div>
        </div>
    )
}

export interface OrderRecipeEditComponentProps {
    OrderGuid: GUID;
    ColourStandardGuid: GUID;
    RecipeGuid?: GUID;
    CanAddComponents?: boolean;
    SeriesId?: number;
    SubstrateId?: number;
    ColourStandardName?: string;
    OnCancelClicked?: () => void;
    OnRecipeSaved?: (recipe: OrderRecipePreviewModel) => void;
}

const FULL_SEARCH_STORAGE_KEY = "recipe_component_full_search";

export function useOrderRecipeEditComponent(props: OrderRecipeEditComponentProps) {
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const {showNotification} = useFlintNotificationContext();
    const {popups} = useVivoPopupContext();
    const [recipeModule, setRecipeModule] = useState<OrderRecipeEditModuleModel>();
    const isFullSearchInitialisedRef = useRef<boolean>(false);
    const [isFullSearch, setIsFullSearch] = useState<boolean>(() => {
        const stored = localStorage.getItem(FULL_SEARCH_STORAGE_KEY);
        isFullSearchInitialisedRef.current = stored !== null;
        return stored !== null ? stored === "true" : false;
    });
    const [totalAmount, setTotalAmount] = useState<number>(0);
    const recipeValidationSchema = useMemo(() => Yup.object().shape({
        CodeGeneratorNumber: Yup.string().optional().max(50, t("RECIPES_VALIDATION_CodeGeneratorNumberMax")),
        SapCode: Yup.string().nullable().max(8, t("RECIPES_VALIDATION_SapCodeMax")),
        UnitOfMeasure: Yup.object().shape({
            Id: Yup.number()
                .typeError(t("RECIPES_VALIDATION_UnitOfMeasureRequired"))
                .required(t("RECIPES_VALIDATION_UnitOfMeasureRequired"))
                .min(1, t("RECIPES_VALIDATION_UnitOfMeasureRequired")),
            Name: Yup.string().required(t("RECIPES_VALIDATION_UnitOfMeasureRequired")),
        }).required(t("RECIPES_VALIDATION_UnitOfMeasureRequired")),
        //Description: Yup.string().required("Description is required!"),
        Components: Yup.array()
            .min(1, t("RECIPES_VALIDATION_ComponentRequired"))
            .of(
                Yup.object().shape({
                    InkItemName: Yup.string().required(t("RECIPES_VALIDATION_ComponentNameRequired")),
                    InkItemDescription: Yup.string().nullable(),
                    Amount: Yup.number().required(t("RECIPES_VALIDATION_AmountRequired")).min(0.01, t("RECIPES_VALIDATION_AmountPositive")),
                })
            ),
    }), [t]);
    const {
        formErrors: recipeFormErrors,
        validateForm: recipeValidateForm,
        scrollToFirstInvalid
    } = useVivoValidation(recipeValidationSchema);

    useEffect(() => {
        const controller = new AbortController(); // Controller for request cancellation
        loadData(controller.signal).catch(() => {
        }); // Catching errors for unhandled promise warning
        return () => controller.abort(); // Cleanup on unmount
    }, []);

    async function loadData(signal: AbortSignal) {
        setIsLoading(true);
        const params = {orderGuid: props.OrderGuid, csGuid: props.ColourStandardGuid, recipeGuid: props.RecipeGuid};
        try {
            const response = await axios.get<ApiResponseDto<OrderRecipeEditModuleModel>>(ApiUrl_Orders_GetOrderRecipe,
                {params: params, signal: signal});
            const model = response.data.Result;
            if (!model.Recipe.UnitOfMeasure || (model.Recipe.UnitOfMeasure.Id ?? 0) === 0) {
                model.Recipe.UnitOfMeasure = model.UnitOfMeasureList[0];
            }
            if (model.Recipe.Components.length === 0)
                model.Recipe.Components.push(createNewComponent());
            if (!isFullSearchInitialisedRef.current) {
                const defaultFullSearch = !model.ByDefaultSearchComponentsInCurrentSeriesOnly;
                setIsFullSearch(defaultFullSearch);
                isFullSearchInitialisedRef.current = true;
            }
            setRecipeModule(model);
        } catch (err: any) {
            if (axios.isCancel(err)) {
                // console.log("Request canceled");
            } else {
                showNotification(err);
            }
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (!recipeModule?.Recipe.Components) {
            setTotalAmount(0);
            return;
        }
        setTotalAmount(recipeModule?.Recipe.Components.reduce((acc, c) => acc + c.Amount, 0));
    }, [recipeModule?.Recipe.Components])

    function createNewComponent(): OrderRecipeComponentEditModel {
        return {
            Id: 0,
            Guid: NewGuid(),
            InkItemName: "",
            InkItemDescription: "",
            Amount: 0,
        };
    }

    function handleAddNewComponentClicked() {
        setRecipeModule(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                Recipe: {
                    ...prev.Recipe,
                    Components: [...prev.Recipe.Components, createNewComponent()],
                },
            };
        });
    }


    function handleComponentDeleted(index: number) {
        setRecipeModule(prev => {
            if (!prev) return prev;
            const components = prev.Recipe.Components.filter((_, i) => i !== index);
            return {...prev, Recipe: {...prev.Recipe, Components: components}};
        });
    }

    function handleOnRecipeUpdated<T extends keyof OrderRecipeEditModel>(key: T, value: OrderRecipeEditModel[T]) {
        setRecipeModule(prev => {
            if (!prev) return prev; // nothing to do
            return {
                ...prev,
                Recipe: {
                    ...prev?.Recipe,
                    [key]: value
                }
            }
        });
    }

    async function handleGenerateDescriptionClicked() {
        try {
            const description = await popups.recipeDescriptionGeneratorPopup.start({
                OrderGuid: props.OrderGuid,
                InitialInkSerieId: effectiveSeriesId,
                InitialSubstrateId: effectiveSubstrateId,
                InitialColourStandardName: effectiveColourStandardName,
            });
            handleOnRecipeUpdated("Description", description);
        } catch {
            // user cancelled
        }
    }

    function handleOnComponentUpdated<T extends keyof OrderRecipeComponentEditModel>
    (index: number, key: T, value: OrderRecipeComponentEditModel[T]) {
        setRecipeModule(prev => {
            if (!prev) return prev; // nothing to do
            const comps = prev.Recipe.Components;
            if (index < 0 || index >= comps.length) return prev; // out of range

            // clone the array and the item at index
            const nextComponents = comps.slice();
            const current = nextComponents[index];
            nextComponents[index] = {
                ...current,
                [key]: value, // strongly typed by <T>
            };

            return {
                ...prev,
                Recipe: {...prev.Recipe, Components: nextComponents},
            };
        });
    }

    async function validateForm(): Promise<boolean> {
        try {
            await recipeValidateForm(recipeModule?.Recipe);
            return true;
        } catch (error: any) {
            showNotification(error, {style: "error"});
            setTimeout(() => scrollToFirstInvalid(), 50);
            return false;
        }
    }

    function MapToOrderRecipeSaveRequest(recipe?: OrderRecipeEditModel): OrderRecipeSaveRequest {
        if (!recipe)
            throw "Recipe  is not loaded!";

        return {
            ...recipe,
            OrderGuid: props.OrderGuid,
            ColorStandardId: recipe.ColourStandardId,
            UnitOfMeasureId: recipe.UnitOfMeasure.Id,
            Components: recipe.Components.map(c => MapToOrderRecipeComponentSaveRequest(c))
        }
    }

    function MapToOrderRecipeComponentSaveRequest(component: OrderRecipeComponentEditModel): OrderRecipeComponentSaveRequest {
        return {
            Id: component.Id,
            Guid: component.Guid,
            InkItemGuid: component.InkItemGuid,
            InkItemName: component.InkItemName,
            InkItemDescription: component.InkItemDescription,
            Amount: component.Amount,
        }
    }

    async function handleSaveRecipeClicked() {
        if (!await validateForm())
            return;
        setIsLoading(true);
        try {
            const post = MapToOrderRecipeSaveRequest(recipeModule?.Recipe);
            const result = await axios.post<ApiResponseDto<OrderRecipePreviewModel>>(ApiUrl_Orders_SaveOrderRecipe, post);
            props.OnRecipeSaved?.(result.data.Result);
        } catch (error: any) {
            showNotification(error);
        } finally {
            setIsLoading(false);
        }
    }

    function handleCancelClicked() {
        props.OnCancelClicked?.();
    }

    function handleFullSearchToggled(value: boolean) {
        setIsFullSearch(value);
        localStorage.setItem(FULL_SEARCH_STORAGE_KEY, String(value));
    }

    const isLabDivisionMismatch = props.CanAddComponents === false;

    // Caller-supplied props win when present. Module fallbacks fire only when
    // a prop is undefined — the Recipe List flow has no order-item state, so
    // it relies entirely on the BE-resolved OrderItem* fields. Empty CS name
    // (`""`) falls through via logical OR so the user sees an empty input
    // instead of literal "".
    const effectiveSeriesId = props.SeriesId ?? recipeModule?.OrderItemInkSerie?.Id;
    const effectiveSubstrateId = props.SubstrateId ?? recipeModule?.OrderItemSubstrate?.Id;
    const effectiveColourStandardName = props.ColourStandardName || recipeModule?.ColourStandardName || undefined;

    return {
        state: {
            isLoading: {get: isLoading},
            recipe: {get: recipeModule?.Recipe},
            totalAmount: {get: totalAmount},
            isFullSearch: {get: isFullSearch},
        },
        utils: {
            unitsOfMeasure: recipeModule?.UnitOfMeasureList ?? [],
            isLabDivisionMismatch,
            hasSeriesContext: (effectiveSeriesId ?? 0) > 0,
            effectiveSeriesId,
            validators: {
                recipeValidator: {
                    validateForm: recipeValidateForm,
                    formErrors: recipeFormErrors,
                }
            },
        },
        handlers: {
            handleOnRecipeUpdated,
            handleAddNewComponentClicked,
            handleComponentDeleted,
            handleOnComponentUpdated,
            handleSaveRecipeClicked,
            handleCancelClicked,
            handleFullSearchToggled,
            handleGenerateDescriptionClicked,
        }
    }
}
